import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
    input,
    computed,
    signal,
    linkedSignal,
} from "@angular/core";
import { Table } from "../Table";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25Number } from "../../../util/core/s25.number";

@TypeManagerDecorator("s25-ng-table-list")
@Component({
    selector: "s25-ng-table-list",
    template: `
        @if (!ordered() && items()?.length) {
            <ul [class.hasDecoration]="hasDecoration()">
                <ng-container *ngTemplateOutlet="list"></ng-container>
            </ul>
        }
        @if (ordered() && items()?.length) {
            <ol [class.hasDecoration]="hasDecoration()">
                <ng-container *ngTemplateOutlet="list"></ng-container>
            </ol>
        }

        <ng-template #list>
            @for (cell of items() | slice: 0 : visibleRows(); track cell.item) {
                <li [class.lastOfSet]="cell.lastOfSet">
                    <s25-ng-table-cell [defaults]="cell.item" [row]="row()"></s25-ng-table-cell>
                </li>
            }
        </ng-template>

        @if (!showAll()) {
            <div class="button-group">
                <button
                    [disabled]="visibleRows() >= items().length"
                    (click)="onShowMore()"
                    class="aw-button aw-button--outline"
                >
                    More
                </button>
                <button
                    [disabled]="visibleRows() <= pageSize()"
                    (click)="onShowLess()"
                    class="aw-button aw-button--outline"
                >
                    Less
                </button>
            </div>
        }
    `,
    styles: `
        ul {
            margin: auto;
            text-align: left;
            padding-inline-start: 1.1em;
        }

        ul:not(.hasDecoration) {
            list-style-type: none;
            padding-inline-start: 0;
        }

        ::ng-deep s25-ng-table-list li > * {
            display: block;
        }

        .lastOfSet:not(:last-child)::after {
            content: "";
            display: block;
            width: 0.4em;
            aspect-ratio: 1;
            background-color: var(--cn-blue);
            border-radius: 50%;
            margin-inline: auto;
            margin-block: 0.25rem;
        }

        .button-group {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5em;
            margin: 0.5em 0;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class GenericTableListComponent {
    private static readonly DEFAULT_PAGE_SIZE = 5;

    readonly ordered = input(false);
    readonly itemsInput = input<Table.Cell[] | Table.Cell[][]>([], { alias: "items" });
    readonly row = input<Table.Row>();
    // Default number of visible rows
    readonly visibleRowsInput = input(GenericTableListComponent.DEFAULT_PAGE_SIZE, { alias: "visibleRows" });
    readonly pageSizeInput = input<number>(undefined, { alias: "pageSize" });
    readonly hasDecoration = input(true);
    readonly hasPagination = input(true);
    // If there's just this many more items, show them instead of hiding behind "more" button
    readonly flex = input(1);

    private readonly visiblePages = signal(0);

    protected readonly items = computed(() =>
        this.itemsInput()?.flatMap((item) => {
            if (Array.isArray(item)) return item.map((item, i, arr) => ({ item, lastOfSet: i === arr.length - 1 }));
            return { item, lastOfSet: false };
        }),
    );
    protected readonly pageSize = computed(
        () => this.pageSizeInput() ?? this.visibleRowsInput() ?? GenericTableListComponent.DEFAULT_PAGE_SIZE,
    );
    protected readonly showAll = computed(() => {
        if (!this.hasPagination()) return true;
        const visible = this.visibleRowsInput() ?? GenericTableListComponent.DEFAULT_PAGE_SIZE;
        const total = this.items()?.length ?? 0;
        return total - visible <= this.flex();
    });
    protected readonly visibleRows = linkedSignal(() => {
        if (this.showAll()) return this.items()?.length ?? 0;
        const total = this.items()?.length ?? 0;
        const target = (this.visibleRowsInput() ?? 5) + this.visiblePages() * this.pageSize();
        const visible = S25Number.clamp(target, 0, total);
        if (total - visible <= this.flex()) return total;
        return visible;
    });

    onShowMore() {
        this.visiblePages.update((visible) => visible + 1);
    }

    onShowLess() {
        this.visiblePages.update((visible) => visible - 1);
    }
}
